import { Link } from "react-router-dom";
import type { Campaign } from "../api/client";
import { campaignTypeLabel, formatDayShort, formatTimeParts } from "../lib/format";

export type CampaignTileProps = {
  campaign: Campaign;
};

function ChannelIcons({ campaign }: { campaign: Campaign }) {
  const types = campaign.types || {
    email: campaign.channel !== "sms",
    sms: campaign.channel === "sms" || campaign.channel === "Email + SMS",
  };
  const isManual = !campaign.misc?.type || campaign.misc.type === "manual";
  const sent = Boolean(campaign.successfully_sent || campaign.status === "sent");

  const emailClass = isManual
    ? sent
      ? "sent"
      : "draft"
    : "on";
  const smsClass = isManual
    ? sent
      ? "sent"
      : "draft"
    : "on";

  return (
    <div className="ne__tile__top-icons">
      {types.email ? (
        <i
          className={`ne__tile__top-icon ne__icon__mail--fill ${emailClass}`}
          title={isManual ? (sent ? "Email sent" : "Email Draft") : "Email is ON"}
        />
      ) : null}
      {types.sms ? (
        <i
          className={`ne__tile__top-icon ne__icon__message--fill ${smsClass}`}
          title={isManual ? (sent ? "SMS sent" : "SMS Draft") : "SMS is ON"}
        />
      ) : null}
    </div>
  );
}

/** Historical campaign tile (`campaigns/partials/tile-primary.html`). */
export function CampaignTile({ campaign }: CampaignTileProps) {
  const isManual = !campaign.misc?.type || campaign.misc.type === "manual";
  const isDraft =
    isManual && !(campaign.successfully_sent || campaign.status === "sent");
  const updated = campaign.updated_at || campaign.created_at;
  const { time, period } = formatTimeParts(updated);

  // Angular structure: ul.ne__tile__grid > li > div.ne__tile
  return (
    <li>
      <div className="ne__tile ne__tile--primary">
        <Link
          className="ne__tile__button"
          to={`/app/campaigns/${campaign.id}/edit/email`}
        >
          <div className="ne__tile__content">
            <div className="ne__tile__top">
              <ChannelIcons campaign={campaign} />
            </div>
            <div className="ne__tile__body">
              <h3 className="ne__tile__name">{campaign.name}</h3>
            </div>
            <div className="ne__tile__bottom">
              <ul className="ne__tile__info">
                <li className="ne__tile__info--type">
                  <span>{campaignTypeLabel(campaign)}</span>
                </li>
                {isDraft ? (
                  <li className="ne__tile__info--draft">
                    <strong>Draft</strong>
                    {updated ? (
                      <span className="ne__tile__updated-date">
                        {" "}
                        - Updated {formatDayShort(updated)}
                        {time ? (
                          <>
                            {" "}
                            at {time}
                            <span className="ne__period"> {period}</span>
                          </>
                        ) : null}
                      </span>
                    ) : null}
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
        </Link>
        {/* Historical tile-primary always renders options row (opacity 0 until
            hover) — without it tile height is 225px vs Angular 272px. */}
        <ul className="ne__tile__options">
          <li>
            <Link
              className="ne__tile__option"
              to={`/app/campaigns/${campaign.id}/edit/email`}
              aria-label="Duplicate"
              title="Duplicate"
            >
              <i className="ne__icon__duplicate--thin" />
            </Link>
          </li>
        </ul>
      </div>
    </li>
  );
}

export type CampaignGridProps = {
  campaigns: Campaign[];
};

export function CampaignGrid({ campaigns }: CampaignGridProps) {
  return (
    <ul className="ne__tile__grid">
      {campaigns.map((campaign) => (
        <CampaignTile key={campaign.id} campaign={campaign} />
      ))}
      <li>
        <div className="ne__tile ne__tile--new">
          <Link className="ne__tile__button" to="/app/campaigns/new/edit/email">
            <div className="ne__tile__content">
              <div className="ne__tile__body">
                <i className="ne__tile__icon ne__icon__add--thinnest" />
                <h3 className="ne__tile__name">Create new campaign</h3>
              </div>
            </div>
          </Link>
        </div>
      </li>
    </ul>
  );
}
